import { usePlotArea, useXAxisScale } from "recharts";

/** One start–stop block the employee declared on their timesheet (karta godzin). */
export interface DeclaredPeriod {
  date: string;
  start: string;
  end: string;
  kind: "uop" | "uz";
  label: string;
}

/** A declared block projected onto the chart's x-axis, in axis value units. */
export interface DeclaredMark {
  key: string;
  color: string;
  x1: number;
  x2: number;
  /** Vertical slot, counted upwards from the bottom of the plot area. */
  lane: number;
  title: string;
}

/** "HH:MM" → hours as a float (8:30 → 8.5). Null when unparseable. */
export function hhmmToHours(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value ?? "");
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60;
}

/** "YYYY-MM-DD" + "HH:MM" → local epoch ms, matching the request timestamps. */
export function declaredToTs(date: string, hhmm: string): number | null {
  const t = new Date(`${date}T${hhmm}:00`).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Declared start/stop dots drawn at the bottom of the plot area, one lane per
 * series. Meant to be mounted through recharts' <Customized> so it can read the
 * chart's x scale and plot area.
 */
export function DeclaredMarks({ marks }: { marks: DeclaredMark[] }) {
  const xScale = useXAxisScale();
  const plot = usePlotArea();
  if (!xScale || !plot || marks.length === 0) return null;

  const left = plot.x;
  const right = plot.x + plot.width;

  return (
    <g data-testid="declared-marks">
      {marks.map((m) => {
        const rawStart = xScale(m.x1);
        const rawEnd = xScale(m.x2);
        if (rawStart == null || rawEnd == null) return null;
        // Blocks that started before / end after the visible window are clamped
        // to the edge; the dot is dropped so it can't read as a real boundary.
        const startVisible = rawStart >= left && rawStart <= right;
        const endVisible = rawEnd >= left && rawEnd <= right;
        if (!startVisible && !endVisible && !(rawStart < left && rawEnd > right)) {
          return null;
        }
        const x1 = Math.min(Math.max(rawStart, left), right);
        const x2 = Math.min(Math.max(rawEnd, left), right);
        const y = plot.y + plot.height - 5 - m.lane * 9;
        return (
          <g key={m.key}>
            <title>{m.title}</title>
            <line
              x1={x1}
              x2={x2}
              y1={y}
              y2={y}
              stroke={m.color}
              strokeWidth={2}
              strokeOpacity={0.45}
              strokeLinecap="round"
            />
            {startVisible && (
              <circle
                data-testid="declared-start"
                cx={x1}
                cy={y}
                r={3.5}
                fill={m.color}
                fillOpacity={0.9}
              />
            )}
            {endVisible && (
              <circle
                data-testid="declared-end"
                cx={x2}
                cy={y}
                r={3.5}
                fill="var(--color-bg-surface, #fff)"
                stroke={m.color}
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}
